import React, { useState } from 'react';
import {
    Box, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, Stack, Grid, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
    Autocomplete, FormControlLabel, Checkbox, Chip, Tooltip, Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';

const REPORT_TYPE_OPTIONS = [
    { value: 'chapter5', label: 'Chapter 5 — Restraint & Confiscation (curator bonis over Defendants’ property)' },
    { value: 'chapter6', label: 'Chapter 6 — Preservation & Forfeiture (ex-parte, curator over specific property)' },
];

// Some matters run to 20+ reports before finalisation, so the report title isn't a fixed
// list - it's a free-text field with ordinal suggestions ("1st Report" .. "30th Report")
// plus the common special-case labels. Users can still type anything not in the list.
const getOrdinal = (n) => {
    const rem100 = n % 100;
    if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
    switch (n % 10) {
        case 1: return `${n}st`;
        case 2: return `${n}nd`;
        case 3: return `${n}rd`;
        default: return `${n}th`;
    }
};
const REPORT_TITLE_SUGGESTIONS = [
    'Interim Report',
    ...Array.from({ length: 30 }, (_, i) => `${getOrdinal(i + 1)} Report`),
    'Quarterly Report',
    'Final Report',
];

const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-ZA');
};

const emptyReportForm = () => ({
    reportType: 'chapter5',
    reportTitle: '',
    dateOfOrder: '',
    annexures: [],
    // Chapter 5 - per-report narrative
    reportNarrative: '',
    respondentNarratives: {},
    // Chapter 6 - per-report narrative
    priorReportsSummary: '',
    serviceExecutionNarrative: '',
    assetNarratives: {},
    gazettePublicationDate: '',
    gazetteReference: '',
    forfeitureStatus: '',
    nextReportDueDate: '',
});

const getAssetLabel = (asset) => asset.asset_description || asset.streetAddress || asset.defName || `Asset #${asset.id}`;

export default function CuratorReportTab({ formData, handleFieldChange, isEditingMode, user, matterId }) {
    const reports = formData?.curatorReports || [];
    const respondents = formData?.respondents || [];

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogStep, setDialogStep] = useState(1); // 1 = type/title/date/annexures, 2 = Report Details
    const [editingId, setEditingId] = useState(null);
    const [reportForm, setReportForm] = useState(emptyReportForm());
    const [matterDocuments, setMatterDocuments] = useState([]);
    const [docsLoading, setDocsLoading] = useState(false);
    const [matterAssets, setMatterAssets] = useState([]);
    const [assetsLoading, setAssetsLoading] = useState(false);

    const isDisabled = !isEditingMode || user?.role === 'guest';

    const saveToDB = async (list) => {
        if (!matterId) return true;
        try {
            const response = await fetch(`/api/legal-matter/${matterId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ curatorReports: list }),
            });
            // fetch() does NOT throw on HTTP error statuses (unlike axios) - it only
            // rejects on network failure - so this check is required to actually detect
            // a rejected/failed save instead of silently treating it as a success.
            if (!response.ok) {
                const body = await response.text().catch(() => '');
                console.error("Auto-save failed", response.status, body);
                window.alert(`Failed to save this report to the server (status ${response.status}). It will not persist after a reload - please try again or contact support.`);
                return false;
            }
            return true;
        } catch (e) {
            console.error("Auto-save failed", e);
            window.alert("Failed to save this report to the server (network error). It will not persist after a reload - please try again.");
            return false;
        }
    };

    const fetchMatterDocuments = async () => {
        if (!matterId) return;
        setDocsLoading(true);
        try {
            const res = await fetch(`/api/cashbook/available-documents/${matterId}`);
            const data = res.ok ? await res.json() : [];
            setMatterDocuments(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Failed to fetch matter documents for annexures", e);
            setMatterDocuments([]);
        } finally {
            setDocsLoading(false);
        }
    };

    const fetchMatterAssets = async () => {
        if (!matterId) return;
        setAssetsLoading(true);
        try {
            const res = await fetch(`/api/immoveables?legalMatterId=${matterId}`);
            const data = res.ok ? await res.json() : [];
            setMatterAssets(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Failed to fetch matter assets for report details", e);
            setMatterAssets([]);
        } finally {
            setAssetsLoading(false);
        }
    };

    const handleOpenAdd = () => {
        setReportForm(emptyReportForm());
        setEditingId(null);
        setDialogStep(1);
        setDialogOpen(true);
        fetchMatterDocuments();
        fetchMatterAssets();
    };

    const handleEditReport = (report) => {
        setReportForm({ ...emptyReportForm(), ...report });
        setEditingId(report.id);
        setDialogStep(1);
        setDialogOpen(true);
        fetchMatterDocuments();
        fetchMatterAssets();
    };

    const toggleAnnexure = (doc) => {
        setReportForm(prev => {
            const already = (prev.annexures || []).some(a => a.filepath === doc.filepath);
            const annexures = already
                ? prev.annexures.filter(a => a.filepath !== doc.filepath)
                : [...(prev.annexures || []), { filepath: doc.filepath, label: doc.fileName }];
            return { ...prev, annexures };
        });
    };

    const updateReportField = (field, value) => setReportForm(prev => ({ ...prev, [field]: value }));

    const updateRespondentNarrative = (id, field, value) => {
        setReportForm(prev => ({
            ...prev,
            respondentNarratives: {
                ...prev.respondentNarratives,
                [id]: { ...(prev.respondentNarratives?.[id] || {}), [field]: value },
            },
        }));
    };

    const updateAssetNarrative = (id, value) => {
        setReportForm(prev => ({
            ...prev,
            assetNarratives: { ...prev.assetNarratives, [id]: value },
        }));
    };

    const handleSaveReport = () => {
        const updatedList = editingId
            ? reports.map(r => (r.id === editingId ? { ...reportForm, id: editingId } : r))
            : [...reports, { id: -Math.floor(Math.random() * 1000000), ...reportForm }];
        handleFieldChange({ target: { name: 'curatorReports', value: updatedList, type: 'array' } });
        saveToDB(updatedList);
        setDialogOpen(false);
    };

    const handleDeleteReport = (id) => {
        if (!window.confirm('Are you sure you want to delete this report entry?')) return;
        const updatedList = reports.filter(r => r.id !== id);
        handleFieldChange({ target: { name: 'curatorReports', value: updatedList, type: 'array' } });
        saveToDB(updatedList);
    };

    const buildDownloadParams = (report) => {
        const params = new URLSearchParams({
            matterId,
            reportId: report.id,
            reportType: report.reportType || 'chapter5',
            reportTitle: report.reportTitle,
            dateOfOrder: report.dateOfOrder || '',
        });
        if (Array.isArray(report.annexures) && report.annexures.length > 0) {
            params.set('annexures', JSON.stringify(report.annexures));
        }
        return params;
    };

    const handleDownload = (report) => {
        window.open(`/api/npa/curator-report/download?${buildDownloadParams(report).toString()}`, '_blank');
    };

    const reportTypeLabel = (type) => REPORT_TYPE_OPTIONS.find(o => o.value === type)?.label.split(' — ')[0] || 'Chapter 5';

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Curator's Reports
                </Typography>
                <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={handleOpenAdd} disabled={isDisabled}>
                    New Report
                </Button>
            </Stack>

            {/* Matter reference header - pulled from Estate Information so it's always in sync */}
            <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Name of Matter</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formData?.matterName || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Master Ref</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formData?.estateNo || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Case No</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formData?.caseNumber || '—'}</Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Each report generates a document pre-filled with case details, the defendants/respondents list, and the
                asset schedule already captured in this matter. Fill in the "Report Details" step so the narrative
                sections (background, service of documents, etc.) come through in the downloaded PDF instead of
                staying blank.
            </Typography>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Report</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Order Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Annexures</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {reports.map((report) => (
                            <TableRow key={report.id} hover>
                                <TableCell>{report.reportTitle}</TableCell>
                                <TableCell>{reportTypeLabel(report.reportType)}</TableCell>
                                <TableCell>{formatDateForDisplay(report.dateOfOrder) || '-'}</TableCell>
                                <TableCell>
                                    {Array.isArray(report.annexures) && report.annexures.length > 0 ? (
                                        <Tooltip title={report.annexures.map(a => a.label).join(', ')}>
                                            <Chip size="small" icon={<AttachFileIcon />} label={report.annexures.length} />
                                        </Tooltip>
                                    ) : '-'}
                                </TableCell>
                                <TableCell align="center">
                                    <Stack direction="row" spacing={1} justifyContent="center">
                                        <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleEditReport(report)} disabled={isDisabled}>
                                            Details
                                        </Button>
                                        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleDownload(report)}>
                                            Download
                                        </Button>
                                        <IconButton size="small" color="error" onClick={() => handleDeleteReport(report.id)} disabled={isDisabled}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                        {reports.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary', fontStyle: 'italic' }}>
                                    No reports recorded yet. Click "New Report" to add one.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth={dialogStep === 1 ? 'sm' : 'md'}>
                <DialogTitle>
                    {editingId ? 'Edit Curator\'s Report' : 'New Curator\'s Report'}
                    {' — '}
                    {dialogStep === 1 ? 'Step 1: Report' : 'Step 2: Report Details'}
                </DialogTitle>
                <DialogContent>
                    {dialogStep === 1 && (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Report Type (POCA Chapter)"
                                    value={reportForm.reportType}
                                    onChange={(e) => updateReportField('reportType', e.target.value)}
                                >
                                    {REPORT_TYPE_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete
                                    freeSolo
                                    options={REPORT_TITLE_SUGGESTIONS}
                                    value={reportForm.reportTitle}
                                    onChange={(e, newValue) => updateReportField('reportTitle', newValue || '')}
                                    onInputChange={(e, newInputValue) => updateReportField('reportTitle', newInputValue || '')}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Report" placeholder="e.g. 1st Report, 21st Report, Interim Report..." fullWidth />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label={reportForm.reportType === 'chapter6' ? 'Date of Preservation Order' : 'Date of Provisional Restraint Order'}
                                    InputLabelProps={{ shrink: true }}
                                    value={reportForm.dateOfOrder}
                                    onChange={(e) => updateReportField('dateOfOrder', e.target.value)}
                                    inputProps={{ max: '9999-12-31' }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Annexures (tick to attach automatically at the end of the downloaded PDF)
                                </Typography>
                                <Paper variant="outlined" sx={{ maxHeight: 220, overflowY: 'auto', p: 1 }}>
                                    {docsLoading ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>Loading documents...</Typography>
                                    ) : matterDocuments.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                                            No documents found for this matter yet. Upload documents on the Documents tab first.
                                        </Typography>
                                    ) : (
                                        matterDocuments.map((doc) => (
                                            <FormControlLabel
                                                key={doc.id}
                                                sx={{ display: 'flex', width: '100%', ml: 0 }}
                                                control={
                                                    <Checkbox
                                                        checked={(reportForm.annexures || []).some(a => a.filepath === doc.filepath)}
                                                        onChange={() => toggleAnnexure(doc)}
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2">
                                                        {doc.fileName}{' '}
                                                        <Typography component="span" variant="caption" color="text.secondary">({doc.uploadDate})</Typography>
                                                    </Typography>
                                                }
                                            />
                                        ))
                                    )}
                                </Paper>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    PDF and image (JPG/PNG) annexures are merged directly into the report PDF. Other file types are
                                    listed as a placeholder page - open them separately from the Documents tab.
                                </Typography>
                            </Grid>
                        </Grid>
                    )}

                    {dialogStep === 2 && (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                    Fill in what you know for this report - anything left blank stays as a red placeholder in the
                                    downloaded PDF for you to complete by hand.
                                </Typography>
                            </Grid>

                            {reportForm.reportType === 'chapter5' ? (
                                <>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            label="This report's own narrative"
                                            placeholder="Property disclosed since the last report, service of documents, dealings with each respondent/defendant, and progress since the last report."
                                            value={reportForm.reportNarrative}
                                            onChange={(e) => updateReportField('reportNarrative', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Dealing with the Defendants / Respondents</Typography>
                                    </Grid>
                                    {respondents.length === 0 && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                No respondents/defendants captured yet - add them on the Estate Information page.
                                            </Typography>
                                        </Grid>
                                    )}
                                    {respondents.map((r) => {
                                        const rn = reportForm.respondentNarratives?.[r.id] || {};
                                        return (
                                            <Grid item xs={12} key={r.id}>
                                                <Paper variant="outlined" sx={{ p: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                        {r.name || 'Unnamed'} ({r.type || 'Respondent'})
                                                    </Typography>
                                                    <Stack spacing={1.5}>
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            minRows={2}
                                                            label="Relationship to other Defendants / involvement with any juristic-person Defendant"
                                                            value={rn.relationship || ''}
                                                            onChange={(e) => updateRespondentNarrative(r.id, 'relationship', e.target.value)}
                                                        />
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            minRows={2}
                                                            label="Allegations against this respondent/defendant"
                                                            value={rn.allegations || ''}
                                                            onChange={(e) => updateRespondentNarrative(r.id, 'allegations', e.target.value)}
                                                        />
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            minRows={2}
                                                            label="Service of the order - attempts, inspection/attachment of assets, further dealings"
                                                            value={rn.service || ''}
                                                            onChange={(e) => updateRespondentNarrative(r.id, 'service', e.target.value)}
                                                        />
                                                    </Stack>
                                                </Paper>
                                            </Grid>
                                        );
                                    })}
                                </>
                            ) : (
                                <>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            label="Prior Interim/Supplementary Reports - dates and recipients"
                                            value={reportForm.priorReportsSummary}
                                            onChange={(e) => updateReportField('priorReportsSummary', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            label="Service and execution of the preservation order"
                                            value={reportForm.serviceExecutionNarrative}
                                            onChange={(e) => updateReportField('serviceExecutionNarrative', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Service &amp; Seizure per Item</Typography>
                                    </Grid>
                                    {assetsLoading ? (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="text.secondary">Loading assets...</Typography>
                                        </Grid>
                                    ) : matterAssets.length === 0 ? (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                No property captured yet - add it on the Assets tab.
                                            </Typography>
                                        </Grid>
                                    ) : (
                                        matterAssets.map((asset) => (
                                            <Grid item xs={12} key={asset.id}>
                                                <Paper variant="outlined" sx={{ p: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                        {getAssetLabel(asset)}
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        minRows={2}
                                                        label="Attempts to serve the order and take possession of this item, and on whom it was served"
                                                        value={reportForm.assetNarratives?.[asset.id] || ''}
                                                        onChange={(e) => updateAssetNarrative(asset.id, e.target.value)}
                                                    />
                                                </Paper>
                                            </Grid>
                                        ))
                                    )}
                                    <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Government Gazette Publication Date"
                                            InputLabelProps={{ shrink: true }}
                                            value={reportForm.gazettePublicationDate}
                                            onChange={(e) => updateReportField('gazettePublicationDate', e.target.value)}
                                            inputProps={{ max: '9999-12-31' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Government Gazette Reference"
                                            value={reportForm.gazetteReference}
                                            onChange={(e) => updateReportField('gazetteReference', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            label="Forfeiture application/order status - Section 50 and CARA account details"
                                            value={reportForm.forfeitureStatus}
                                            onChange={(e) => updateReportField('forfeitureStatus', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Next Report Due Date"
                                            InputLabelProps={{ shrink: true }}
                                            value={reportForm.nextReportDueDate}
                                            onChange={(e) => updateReportField('nextReportDueDate', e.target.value)}
                                            inputProps={{ max: '9999-12-31' }}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    {dialogStep === 2 && <Button onClick={() => setDialogStep(1)}>Back</Button>}
                    {dialogStep === 1 ? (
                        <Button variant="contained" onClick={() => setDialogStep(2)} disabled={!reportForm.reportTitle}>
                            Next: Report Details
                        </Button>
                    ) : (
                        <Button variant="contained" onClick={handleSaveReport}>
                            {editingId ? 'Save Changes' : 'Save Report'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}
